/**
 * Phone Call Summary Modal
 *
 * Modal displayed after a phone call ends to document call outcomes.
 * Allows agents to record tone, difficulty, outcome, and optional PTP/deferment details.
 */

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIcon from '@mui/icons-material/Phone';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { z } from 'zod';

import { Modal } from '@/shared/components/modals';
import { useToast } from '@/shared/components/toasts';
import { useTelephonyHotkeys } from '@/shared/hooks';
import { createZodResolver } from '@/shared/utils/validation/resolvers';
import { useServerFormErrors } from '@/shared/utils/validation';
import { useCallDetail, useSubmitCallDetail, useContactSearch, useCaseSearchForContact } from '../../application/hooks';

import { PtpForm, PTP_API_TO_FORM_FIELD_MAP } from './PtpForm';
import { DefermentForm } from './DefermentForm';
import { RecordingPlayer } from './RecordingPlayer';

import type { QueueModel, SubmitCallDetailRequest, FieldOption, ContactSearchResult, CaseSearchResult } from '../../domain/types';

// ============================================================================
// Validation Schema
// ============================================================================

const callDetailSchema = z
  .object({
    channel: z.string().min(1, 'Channel is required'),
    duration: z.union([z.string(), z.number()]).refine(v => {
      const n = Number(v);
      return !Number.isNaN(n) && n >= 0;
    }, 'Duration must be a valid number'),
    tone: z.string().min(1, 'Tone is required'),
    difficulty: z.string().min(1, 'Difficulty is required'),
    outcome: z.string().min(1, 'Outcome is required'),
    summary: z.string().optional(),
    note: z.string().optional(),
    // These are validated conditionally based on `outcome`
    ptps: z
      .array(
        z.object({
          promised_date: z.string().optional(),
          promised_amount: z.union([z.string(), z.number()]).optional(),
          currency: z.string().optional(),
        })
      )
      .optional(),
    deferment: z
      .object({
        reason: z.string().optional(),
        promised_date: z.string().optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.outcome === 'ptp') {
      const ptps = data.ptps ?? [];
      if (ptps.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ptps'],
          message: 'Please add at least one PTP entry.',
        });
        return;
      }
      ptps.forEach((ptp, index) => {
        if (!ptp.promised_date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['ptps', index, 'promised_date'],
            message: 'Date is required',
          });
        }
        if (!ptp.currency) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['ptps', index, 'currency'],
            message: 'Currency is required',
          });
        }
        const amountValue = ptp.promised_amount;
        if (amountValue === null || amountValue === undefined || amountValue === '' || (typeof amountValue === 'number' && Number.isNaN(amountValue))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['ptps', index, 'promised_amount'],
            message: 'Amount is required',
          });
        } else {
          const amountStr = String(amountValue);
          const [integerPart, decimalPart] = amountStr.split('.');
          const digits = integerPart.replace(/^-/, '');
          if (digits.length > 10) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['ptps', index, 'promised_amount'],
              message: 'Amount must not exceed 10 digits',
            });
          }
          if (decimalPart && decimalPart.length > 2) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['ptps', index, 'promised_amount'],
              message: 'Amount must have at most 2 decimal places',
            });
          }
        }
      });
    }

    if (data.outcome === 'deferment') {
      if (!data.deferment) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['deferment'],
          message: 'Deferment details are required',
        });
        return;
      }
      if (!data.deferment.reason) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['deferment', 'reason'],
          message: 'Reason is required',
        });
      }
      if (!data.deferment.promised_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['deferment', 'promised_date'],
          message: 'Promised date is required',
        });
      }
    }
  });

type CallDetailFormData = z.infer<typeof callDetailSchema>;

const normalizeDateOnly = (value?: string | null) => {
  if (!value) return '';
  return value.split('T')[0].split(' ')[0];
};

// ============================================================================
// Types
// ============================================================================

interface PhoneCallSummaryModalProps {
  open: boolean;
  onClose: () => void;
  queueData: QueueModel | null;
}

// ============================================================================
// Component
// ============================================================================

export function PhoneCallSummaryModal({ open, onClose, queueData }: PhoneCallSummaryModalProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const handleServerErrors = useServerFormErrors(PTP_API_TO_FORM_FIELD_MAP, 'Failed to submit call detail');
  const [selectedOutcome, setSelectedOutcome] = useState('');

  const callId = queueData?.call_id ?? queueData?.id;

  // Fetch call detail
  const { data: callDetail, isLoading: isLoadingDetail, error: callDetailError } = useCallDetail(open ? (callId ?? null) : null);

  // Inbound call: contact/case search state
  const [contactSearchText, setContactSearchText] = useState('');
  const [debouncedContactSearch, setDebouncedContactSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState<ContactSearchResult | null>(null);
  const [selectedCase, setSelectedCase] = useState<CaseSearchResult | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleContactSearchInput = useCallback((text: string) => {
    setContactSearchText(text);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => setDebouncedContactSearch(text), 300);
  }, []);

  const { data: contactResults, isLoading: isLoadingContacts } = useContactSearch(
    open ? (callId ?? null) : null,
    debouncedContactSearch,
  );
  const { data: caseResults, isLoading: isLoadingCases } = useCaseSearchForContact(
    open ? (callId ?? null) : null,
    selectedContact?.contact_id ?? null,
  );

  // Close modal and show toast on call detail fetch error
  useEffect(() => {
    if (callDetailError && open) {
      const message = callDetailError instanceof Error ? callDetailError.message : 'Failed to load call details';
      toast.error(message);
      onClose();
    }
  }, [callDetailError, open, toast, onClose]);

  // Submit mutation
  const { mutate: submitDetail, isPending: isSubmitting } = useSubmitCallDetail();

  // Server-provided options (fallback to empty arrays)
  const toneOptions: FieldOption[] = callDetail?.tone.options ?? [];
  const difficultyOptions: FieldOption[] = callDetail?.difficulty.options ?? [];
  const outcomeOptions: FieldOption[] = callDetail?.outcome.options ?? [];
  const channelOptions: FieldOption[] = callDetail?.channel.options ?? [];

  // Form setup
  const methods = useForm<CallDetailFormData>({
    resolver: createZodResolver(callDetailSchema),
    mode: 'onBlur',
    defaultValues: {
      channel: '',
      duration: 0,
      tone: '',
      difficulty: '',
      outcome: '',
      summary: '',
      note: '',
      ptps: [],
      deferment: undefined,
    },
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  // Watch outcome for conditional rendering
  const outcomeValue = watch('outcome');

  useEffect(() => {
    setSelectedOutcome(outcomeValue || '');
  }, [outcomeValue]);

  // Clear conditional form values when outcome changes away
  useEffect(() => {
    if (selectedOutcome !== 'ptp') {
      setValue('ptps', []);
      clearErrors('ptps');
    }
    if (selectedOutcome !== 'deferment') {
      setValue('deferment', undefined);
      clearErrors('deferment');
    }
  }, [selectedOutcome, setValue, clearErrors]);

  // Pre-populate form when call detail is loaded
  useEffect(() => {
    if (callDetail) {
      const initialOutcome = callDetail.outcome.value || '';
      reset({
        channel: callDetail.channel.value || '',
        duration: callDetail.duration ?? 0,
        tone: callDetail.tone.value || '',
        difficulty: callDetail.difficulty.value || '',
        outcome: initialOutcome,
        summary: callDetail.summary.value || '',
        note: callDetail.note.value || '',
        ptps:
          initialOutcome === 'ptp'
            ? callDetail.ptps.map(ptp => ({
                promised_date: normalizeDateOnly(ptp.promised_date),
                promised_amount: ptp.promised_amount ?? '',
                currency: ptp.currency ?? '',
              }))
            : [],
        deferment:
          initialOutcome === 'deferment' && callDetail.deferment
            ? {
                reason: callDetail.deferment.reason || '',
                promised_date: normalizeDateOnly(callDetail.deferment.promised_date),
              }
            : undefined,
      });
      setSelectedOutcome(initialOutcome);

      // Pre-populate inbound contact/case selectors
      if (callDetail.is_inbound) {
        if (callDetail.current_contact) {
          setSelectedContact(callDetail.current_contact);
          setContactSearchText(callDetail.current_contact.name);
        }
        if (callDetail.case_info) {
          setSelectedCase({
            case_id: callDetail.case_info.case_id,
            case_external_id: callDetail.case_info.case_external_id ?? null,
            loan_id: callDetail.case_info.loan_id ?? null,
            case_status: callDetail.case_info.case_status ?? '',
            amount_due: callDetail.case_info.ammount_due ?? null,
          });
        }
      }
    }
  }, [callDetail, reset]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      reset({
        channel: '',
        duration: 0,
        tone: '',
        difficulty: '',
        outcome: '',
        summary: '',
        note: '',
        ptps: [],
        deferment: undefined,
      });
      setSelectedOutcome('');
      setSelectedContact(null);
      setSelectedCase(null);
      setContactSearchText('');
      setDebouncedContactSearch('');
    }
  }, [open, reset]);

  const onSubmit = handleSubmit(
    data => {
      if (!callId) {
        toast.error('Missing call id. Please close and reopen the modal.');
        return;
      }

      const payload: SubmitCallDetailRequest = {
        channel: data.channel,
        duration: Number(data.duration),
        tone: data.tone,
        outcome: data.outcome,
        difficulty: data.difficulty,
      };

      // Add summary if it has a value
      if (data.summary) {
        payload.summary = data.summary;
      }

      // Only add note if it has a value
      if (data.note) {
        payload.note = data.note;
      }

      // Add PTP data if outcome is ptp
      if (data.outcome === 'ptp' && data.ptps && data.ptps.length > 0) {
        payload.ptps = data.ptps.map(ptp => {
          const amountValue = typeof ptp.promised_amount === 'number' ? ptp.promised_amount : Number.parseFloat(ptp.promised_amount ?? '');
          return {
            promised_date: ptp.promised_date ?? '',
            promised_amount: Number.isNaN(amountValue) ? 0 : amountValue,
            currency: ptp.currency ?? '',
          };
        });
      }

      // Add deferment data if outcome is deferment
      if (data.outcome === 'deferment' && data.deferment) {
        payload.deferment = {
          reason: data.deferment.reason ?? '',
          promised_date: data.deferment.promised_date ?? '',
        };
      }

      // Add inbound contact/case selections
      if (selectedContact) {
        payload.contact_id = selectedContact.contact_id;
      }
      if (selectedCase) {
        payload.case_id = selectedCase.case_id;
      }

      submitDetail(
        { callId, data: payload },
        {
          onSuccess: () => {
            toast.success('Call detail submitted successfully');
            onClose();
          },
          onError: error => {
            console.error('Failed to submit call detail:', error);
            handleServerErrors(error, setError);
            const message = error instanceof Error ? error.message : 'Failed to submit call detail';
            toast.error(message);
          },
        }
      );
    },
    formErrors => {
      console.warn('[PhoneCallSummaryModal] Form validation errors', formErrors);
      toast.error('Please fill in all required fields before submitting.');
    }
  );

  const isLoading = isLoadingDetail;

  // Global keyboard accelerator for the submit action.
  useTelephonyHotkeys({
    onSubmit: open && !isSubmitting && !isLoading && callId ? () => void onSubmit() : undefined,
  });

  const displayCallId = callDetail?.id ?? callId ?? queueData?.id ?? '—';
  const displayCallerName = callDetail?.caller.name ?? queueData?.caller.full_name ?? 'Unknown';
  const displayCallerPhoto = callDetail?.caller.photo_url ?? queueData?.caller.photo_url ?? '';
  const displayDuration = callDetail?.duration != null
    ? `${callDetail.duration}s`
    : '—';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Phone Call Summary"
      icon={PhoneIcon}
      iconColor="primary"
      maxWidth="md"
      fullWidth
      disableBackdropClick={isSubmitting}
      disableEscapeKeyDown={isSubmitting}
      loading={isLoading}
      actions={[
        { label: 'Cancel', onClick: onClose, disabled: isSubmitting },
        {
          label: 'Submit (Alt+S)',
          onClick: onSubmit,
          variant: 'contained',
          color: 'success',
          loading: isSubmitting,
          disabled: isSubmitting || !callId,
        },
      ]}
      actionsStartContent={
        callDetail?.case_info != null ? (
          <Button
            size="small"
            startIcon={<OpenInNewIcon fontSize="small" />}
            onClick={() => {
              onClose();
              navigate(`/case-detail/${String(callDetail.case_info!.case_id)}`);
            }}
          >
            View Case Details
          </Button>
        ) : undefined
      }
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={e => {
            e.preventDefault();
            void onSubmit();
          }}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
        >
          {/* Call Info Section */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1.5,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Call Details
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                  Call ID:
                </Typography>
                <Chip label={displayCallId} size="small" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                  Caller:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Avatar src={displayCallerPhoto ?? ''} sx={{ width: 24, height: 24 }}>
                    {displayCallerName.charAt(0) || 'U'}
                  </Avatar>
                  <Typography variant="body2">{displayCallerName}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                  Duration:
                </Typography>
                <Typography variant="body2">{displayDuration}</Typography>
              </Box>
              {callDetail?.case_info && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                    Loan:
                  </Typography>
                  <Typography variant="body2">{callDetail.case_info.loan_id ?? '—'}</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Inbound Call: Contact & Case Selection */}
          {callDetail?.is_inbound && (
            <Box
              sx={{
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 1.5,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                Link Contact & Case
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Autocomplete
                  size="small"
                  options={contactResults ?? []}
                  value={selectedContact}
                  loading={isLoadingContacts}
                  inputValue={contactSearchText}
                  onInputChange={(_, newValue, reason) => {
                    if (reason === 'input') {
                      handleContactSearchInput(newValue);
                    } else if (reason === 'clear') {
                      setContactSearchText('');
                      setDebouncedContactSearch('');
                    }
                  }}
                  onChange={(_, newValue) => {
                    setSelectedContact(newValue);
                    setSelectedCase(null);
                    if (newValue) {
                      setContactSearchText(newValue.name);
                    }
                  }}
                  getOptionLabel={(option) => {
                    const parts = [option.name];
                    if (option.nric) parts.push(`(${option.nric})`);
                    if (option.phone_number) parts.push(`- ${option.phone_number}`);
                    return parts.join(' ');
                  }}
                  isOptionEqualToValue={(option, val) => option.contact_id === val.contact_id}
                  filterOptions={(x) => x}
                  disabled={isSubmitting}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contact"
                      placeholder="Search by name or NRIC..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.contact_id}>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[option.nric, option.phone_number].filter(Boolean).join(' | ')}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  noOptionsText={contactSearchText.length < 2 ? 'Type at least 2 characters...' : 'No contacts found'}
                />
                <Autocomplete
                  size="small"
                  options={caseResults ?? []}
                  value={selectedCase}
                  loading={isLoadingCases}
                  onChange={(_, newValue) => setSelectedCase(newValue)}
                  getOptionLabel={(option) => {
                    const parts = [];
                    if (option.case_external_id) parts.push(`Case ${option.case_external_id}`);
                    if (option.loan_id) parts.push(`Loan ${option.loan_id}`);
                    parts.push(`(${option.case_status})`);
                    return parts.join(' - ');
                  }}
                  isOptionEqualToValue={(option, val) => option.case_id === val.case_id}
                  disabled={isSubmitting || !selectedContact}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Case"
                      placeholder={selectedContact ? 'Select a case...' : 'Select a contact first'}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingCases ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.case_id}>
                      <Box>
                        <Typography variant="body2">
                          {option.case_external_id ? `Case ${option.case_external_id}` : `Case #${option.case_id}`}
                          {option.loan_id ? ` - Loan ${option.loan_id}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[option.case_status, option.amount_due].filter(Boolean).join(' | ')}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  noOptionsText={selectedContact ? 'No cases found for this contact' : 'Select a contact first'}
                />
              </Box>
            </Box>
          )}

          {/* Recording Player */}
          {callDetail?.recording && (
            <RecordingPlayer recording={callDetail.recording} />
          )}

          {/* Transcript (read-only from AI) */}
          {callDetail?.transcript.value && (
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1.5 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Transcript
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{callDetail.transcript.value}</Typography>
            </Box>
          )}

          {/* Summary (editable, pre-filled from AI) */}
          <TextField
            {...register('summary')}
            label="Summary"
            multiline
            minRows={3}
            fullWidth
            disabled={isSubmitting}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />

          {/* Channel & Duration Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Controller
              name="channel"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.channel} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}>
                  <InputLabel>Channel *</InputLabel>
                  <Select {...field} label="Channel *" disabled>
                    {channelOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.channel && <FormHelperText>{errors.channel.message}</FormHelperText>}
                </FormControl>
              )}
            />

            <TextField
              {...register('duration')}
              label="Duration (seconds)"
              type="number"
              fullWidth
              disabled
              inputProps={{ min: 0, step: '0.1' }}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                '& .MuiInputBase-root': { height: '56px' },
              }}
            />
          </Box>

          {/* Tone & Difficulty Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Controller
              name="tone"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.tone} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}>
                  <InputLabel>Tone *</InputLabel>
                  <Select {...field} label="Tone *" disabled={isSubmitting}>
                    {toneOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.tone && <FormHelperText>{errors.tone.message}</FormHelperText>}
                </FormControl>
              )}
            />

            <Controller
              name="difficulty"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.difficulty} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}>
                  <InputLabel>Difficulty *</InputLabel>
                  <Select {...field} label="Difficulty *" disabled={isSubmitting}>
                    {difficultyOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.difficulty && <FormHelperText>{errors.difficulty.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Box>

          {/* Outcome Dropdown */}
          <Controller
            name="outcome"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.outcome} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}>
                <InputLabel>Outcome *</InputLabel>
                <Select {...field} label="Outcome *" disabled={isSubmitting}>
                  {outcomeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.outcome && <FormHelperText>{errors.outcome.message}</FormHelperText>}
              </FormControl>
            )}
          />

          {/* Conditional PTP Form */}
          {selectedOutcome === 'ptp' && <PtpForm disabled={isSubmitting} />}

          {/* Conditional Deferment Form */}
          {selectedOutcome === 'deferment' && <DefermentForm disabled={isSubmitting} />}

          {/* Note */}
          <TextField
            {...register('note')}
            label="Note"
            fullWidth
            multiline
            rows={2}
            placeholder="Additional notes..."
            disabled={isSubmitting}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
        </Box>
      </FormProvider>
    </Modal>
  );
}
